/**
 * exchange_noti_page_v3.js — DAS-106 Phase 04: wallet.jsx WalletDesktopV2/MobileV2 리디자인
 *
 * 원본 시안: wallet.jsx::WalletDesktopV2 + WalletMobileV2
 * UI: Ops-first 재설계 — 도넛 히어로 + 필터 row + 2-col (입출금/변경 스트림) + 공지 그리드
 *
 * ── 시안 ↔ 운영 도메인 매핑 전략 ──────────────────────────────
 *   시안은 거래소 입출금 상태(지갑) 데이터를 직접 사용.
 *   우리 exchange_noti 도메인과 거의 동일한 의미 구조라 1:1 매핑:
 *     시안 도넛 88.6%/정상   → walletStats.normal_ratio (recharts PieChart)
 *     시안 "전체 코인 · 8 거래소 기준"  → walletStats.total_coins
 *       TODO(DAS-106): "8 거래소 기준" sub — 현재 upbit 단일, 다중 거래소 확장 예정
 *     시안 "정지 코인 · 입금 22 · 출금 18" → walletStats.suspended_count
 *       TODO(DAS-106): 입금/출금 분리 카운트 — BE API 미지원, 보류
 *     시안 "7일 변경 · Delta"  → walletStats.recent_changes_24h (라벨 "24h 변경")
 *       TODO(DAS-106): 7일 집계 → BE recent_changes_7d 확장 예정 시 교체
 *     시안 필터 row (거래소 칩: Upbit/Binance/OKX/Bithumb) → 현재 upbit 단일 (placeholder)
 *       TODO(DAS-106): 다중 거래소 칩 — 거래소 확장 시 활성화
 *     시안 CSV 버튼 → placeholder (데이터 export 기능 미구현)
 *       TODO(DAS-106): wallet 시안 CSV export — 우리는 공지 도메인이라 의미 매핑 보류
 *     시안 입출금 상태 2-col 좌측  → WalletStatusTable 재사용
 *     시안 변경 스트림 2-col 우측  → WalletChangeStream (walletChanges 데이터)
 *     시안 거래지원 종료 공지 2-col 카드 그리드 → DelistingNoticesPanel 재사용
 *     시안 신규 상장 공지 (V2에선 하단 통합) → NewListingNoticesPanel 재사용
 *
 * ── 시안 V2 시각 디테일 (wallet.jsx::WalletDesktopV2) ─────────
 *   .app-body gap       → 24 (V2 기준)
 *   페이지 헤더         → title "Wallet Ops" + subtitle 텍스트
 *   도넛 히어로         → linear-gradient(135deg, surface-card, surface-elevated)
 *                          + radial-gradient(circle at 10% 10%, gain-subtle, transparent 60%)
 *                          → grid: 180px 1fr 1fr 1fr gap:28
 *   도넛               → recharts PieChart (정상/정지, PaddingAngle:2, startAngle:90)
 *   필터 row           → Input + 거래소 칩 + 상태 칩 + flex:1 gap + CSV Btn
 *   2-col grid         → 1.3fr : 1fr, gap:14, alignItems:flex-start
 *   변경 스트림 카드   → StatusDot + coin bold + net mono + field + Badge prev → Badge now
 *   공지 섹션          → 2-col 카드 그리드 (desktop), 리스트 (mobile)
 *
 * ── V1 컴포넌트 재사용 (기능 회귀 0 보장) ──────────────────────
 *   WalletStatusTable, WalletChangeLog, NewListingNoticesPanel,
 *   DelistingNoticesPanel, ServiceConnectionIndicator
 *
 * mock 데이터 미사용 — 모든 수치는 실데이터 API
 * BE gap G2: 변경 스트림은 최근 changes 목록 (실시간 X — DAS-81 폴백 + WS 보강)
 *   // TODO(DAS-65-G2): WebSocket push 확장 검토
 */

import React from 'react';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
// V1 컴포넌트 재사용 (기능 회귀 0 보장)
import WalletStatusTable from '../exchange_noti_page/wallet_status_table';
import WalletChangeLog from '../exchange_noti_page/wallet_change_log';
import DelistingNoticesPanel from '../exchange_noti_page/delisting_notices_panel';
import NewListingNoticesPanel from '../exchange_noti_page/new_listing_notices_panel';
import ServiceConnectionIndicator from '../exchange_noti_page/service_connection_indicator';
import {
    fetchWalletStatus,
    fetchWalletStatusStats,
    fetchWalletStatusChanges,
    fetchNotiHealth,
} from '../../services/exchangeNotiApi';
import { getAccessToken, getStoredUsername, requireLoginOrThrow, clearLoginSession } from '../../utils/auth';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import DashboardWebSocket from '../../services/websocketClient';
import WalletChangeWebSocket from '../../services/walletWebSocketClient';
import { withRouter } from '../../utils/withRouter';
import { showToast } from '../../utils/show_toast';
import { StatusDot, KpiLabel, V2Badge, V2Icon, Btn, Input } from '../common/claude_primitives';
import { getThemeColors } from '../../utils/themeColors';
import { formatKst } from '../../utils/time';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

/* ── 도넛 히어로 차트 (시안 V2 Donut 1:1 — recharts PieChart) ──────────────
 *   정상(gain) / 정지(loss) 비율. 시안: innerRadius 60%, outerRadius ~80%.
 *   모바일: size=88, 데스크톱: size=160
 */
function WalletDonut({ stats, size, isMobile }) {
    const C = getThemeColors();
    const normal = stats?.normal_count ?? 0;
    const suspended = stats?.suspended_count ?? 0;
    const total = (stats?.total_coins ?? (normal + suspended)) || 1;
    const ratio = stats?.normal_ratio ?? (normal / total * 100);

    const donutSize = size || (isMobile ? 88 : 160);
    const innerR = isMobile ? 28 : 52;
    const outerR = isMobile ? 42 : 72;

    const data = [
        { name: '정상', value: normal || 0, color: C.gain },
        { name: '정지', value: suspended || 0, color: C.loss },
    ];

    // 데이터가 모두 0일 때 빈 도넛 방지 — 플레이스홀더 세그먼트
    const chartData = (normal === 0 && suspended === 0)
        ? [{ name: '—', value: 1, color: C.surfaceBorder || 'var(--color-surface-border)' }]
        : data;

    return (
        <div
            style={{ position: 'relative', width: donutSize, height: donutSize, flexShrink: 0 }}
            aria-label={`정상 비율 ${typeof ratio === 'number' ? ratio.toFixed(1) : '—'}%`}
        >
            <ResponsiveContainer width={donutSize} height={donutSize}>
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={innerR}
                        outerRadius={outerR}
                        paddingAngle={2}
                        dataKey="value"
                        strokeWidth={0}
                        startAngle={90}
                        endAngle={-270}
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} opacity={0.85} />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={(value, name) => [`${value}건`, name]}
                        contentStyle={{
                            background: 'var(--color-surface-elevated)',
                            border: '1px solid var(--color-surface-border)',
                            borderRadius: 8,
                            fontSize: 11,
                            color: 'var(--color-content)',
                        }}
                    />
                </PieChart>
            </ResponsiveContainer>
            {/* 도넛 중앙 텍스트 오버레이 */}
            <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
            }}>
                <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: isMobile ? 18 : 32,
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    color: 'var(--color-content)',
                }}>
                    {typeof ratio === 'number' ? ratio.toFixed(1) : '—'}%
                </div>
                <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10,
                    color: 'var(--color-content-muted)',
                    marginTop: 2,
                }}>
                    정상
                </div>
            </div>
        </div>
    );
}

/* ── 변경 스트림 카드 (시안 V2 — Live pill + StatusDot + badge prev→now) ──────
 *   시안: StatusDot + coin bold + net mono muted + field + "·" + Badge prev + arrowR + Badge now
 *   모바일: 5건 슬라이스 / 데스크톱: 6건 슬라이스
 */
function WalletChangeStream({ changes, isLiveWs, isMobile }) {
    // 입출금 필드 한국어 변환
    const fieldLabel = (field) => {
        if (field === 'deposit_status') return '입금';
        if (field === 'withdrawal_status') return '출금';
        return field || '—';
    };

    // 상태값 → tone 변환 (null/빈값 = 신규)
    const statusTone = (status) => {
        if (status === '' || status == null) return 'brand';
        if (status === 'normal') return 'gain';
        return 'loss';
    };

    // 상태값 → 표시 레이블
    const statusLabel = (status) => {
        if (status === '' || status == null) return '신규';
        if (status === 'normal') return '정상';
        return '정지';
    };

    const slice = changes.slice(0, isMobile ? 5 : 6);

    return (
        <div
            style={{
                borderRadius: 16,
                background: 'var(--color-surface-card)',
                border: '1px solid var(--color-surface-border)',
                padding: 18,
                display: 'flex',
                flexDirection: 'column',
            }}
            aria-label="입출금 상태 변경 스트림"
        >
            {/* 섹션 헤더 — "변경 스트림" + Live/폴링 pill */}
            <div style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 12,
            }}>
                <div style={{
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: 'var(--color-content)',
                }}>
                    변경 스트림
                </div>
                {/* Live/폴링 pill (시안 V2 `.pill.on` + refresh icon) */}
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 10px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 500,
                    background: isLiveWs
                        ? 'var(--color-gain-subtle)'
                        : 'var(--color-surface-elevated)',
                    color: isLiveWs
                        ? 'var(--color-gain)'
                        : 'var(--color-content-muted)',
                    border: isLiveWs
                        ? '1px solid color-mix(in oklch, var(--color-gain) 35%, var(--color-surface-border))'
                        : '1px solid var(--color-surface-border)',
                    fontFamily: 'JetBrains Mono, monospace',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                }}>
                    <V2Icon name="refresh" size={10} />
                    {isLiveWs ? 'Live' : '폴링'}
                </span>
            </div>

            {/* 변경 항목 목록 */}
            {slice.length === 0 ? (
                <div style={{
                    padding: '32px 0',
                    textAlign: 'center',
                    color: 'var(--color-content-muted)',
                    fontSize: 12,
                }}>
                    최근 변경 없음
                </div>
            ) : (
                slice.map((c, i) => (
                    <div
                        key={c.id != null ? c.id : i}
                        style={{
                            display: 'flex',
                            gap: 10,
                            padding: '12px 0',
                            borderBottom: i < slice.length - 1
                                ? '1px solid var(--color-surface-border)'
                                : 'none',
                            alignItems: 'flex-start',
                        }}
                    >
                        {/* 상태 점 (new_status 기준) */}
                        <div style={{ marginTop: 4 }}>
                            <StatusDot tone={statusTone(c.new_status)} size={7} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {/* 메인 행: coin · net · field · prev → now */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                flexWrap: 'wrap',
                            }}>
                                <span style={{
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontWeight: 700,
                                    fontSize: 12,
                                    color: 'var(--color-content)',
                                }}>
                                    {c.currency}
                                </span>
                                <span style={{
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: 10,
                                    color: 'var(--color-content-muted)',
                                }}>
                                    {c.net_type || '—'}
                                </span>
                                <span style={{
                                    fontSize: 12,
                                    color: 'var(--color-content-secondary)',
                                }}>
                                    {fieldLabel(c.field)}
                                </span>
                                <span style={{
                                    fontSize: 11,
                                    color: 'var(--color-content-muted)',
                                }}>
                                    ·
                                </span>
                                <V2Badge tone={statusTone(c.old_status)}>
                                    {statusLabel(c.old_status)}
                                </V2Badge>
                                <V2Icon name="arrowR" size={10} />
                                <V2Badge tone={statusTone(c.new_status)}>
                                    {statusLabel(c.new_status)}
                                </V2Badge>
                            </div>
                            {/* 타임스탬프 */}
                            <div style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 10,
                                color: 'var(--color-content-muted)',
                                marginTop: 4,
                            }}>
                                {c.detected_at ? formatKst(c.detected_at) : '—'}
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

/* ── ExchangeNotiPageV3 메인 클래스 컴포넌트 ──────────────────────────── */
class ExchangeNotiPageV3 extends React.Component {
    constructor(props) {
        super(props);
        const sp = props.searchParams || new URLSearchParams();
        this.state = {
            collapsed: getNavCollapsed(),
            isMobile: isMobileViewport(),
            // 지갑 상태
            walletStatus: [],
            walletStatusLoading: true,
            walletStatusError: false,
            walletStatusLastUpdated: null,
            walletSearchQuery: '',
            // URL 쿼리 복원 (V1 DEFAULTS 와 완전 일치)
            walletStatusFilter: sp.get('status') || 'suspended',
            walletListedOnly: sp.get('listed') !== 'false',
            walletMarketFilter: sp.get('market') || null,
            // 통계
            walletStats: null,
            walletStatsLoading: true,
            walletStatsError: false,
            // 변경 이력
            walletChanges: [],
            walletChangesLoading: true,
            walletChangesError: false,
            walletChangesPagination: { count: 0, next: null, previous: null },
            walletChangesOffset: 0,
            walletChangesFilterCurrency: sp.get('curr') || '',
            walletChangesRange: sp.get('range') || '7d',
            walletChangesKind: sp.get('kind') || 'all',
            // WS + 서비스 헬스
            wsStatus: 'disconnected',
            isPollingFallback: false,
            serviceHealthStatus: 'loading',
            // 실시간 하이라이트
            walletHighlightIds: [],
        };
        this.dashboardWs = null;
        this.walletPollingInterval = null;
        this._resizeListener = null;
        this.walletChangeWs = null;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        this.connectWebSocket();
        this.connectWalletChangeWs();
        this.fetchStatsData();
        this.fetchChangesData();
        this.fetchServiceHealth();
        this._resizeListener = () => this.setState({ isMobile: isMobileViewport() });
        window.addEventListener('resize', this._resizeListener);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.searchParams !== this.props.searchParams) {
            const sp = this.props.searchParams;
            const nextStatus = sp.get('status') || 'suspended';
            const nextListed = sp.get('listed') !== 'false';
            const nextMarket = sp.get('market') || null;
            const nextCurr = sp.get('curr') || '';
            const nextRange = sp.get('range') || '7d';
            const nextKind = sp.get('kind') || 'all';
            if (
                nextStatus !== this.state.walletStatusFilter ||
                nextListed !== this.state.walletListedOnly ||
                nextMarket !== this.state.walletMarketFilter ||
                nextCurr !== this.state.walletChangesFilterCurrency ||
                nextRange !== this.state.walletChangesRange ||
                nextKind !== this.state.walletChangesKind
            ) {
                this.setState({
                    walletStatusFilter: nextStatus,
                    walletListedOnly: nextListed,
                    walletMarketFilter: nextMarket,
                    walletChangesFilterCurrency: nextCurr,
                    walletChangesRange: nextRange,
                    walletChangesKind: nextKind,
                    walletChangesOffset: 0,
                }, () => {
                    const changesChanged = (
                        nextCurr !== (prevProps.searchParams.get('curr') || '') ||
                        nextRange !== (prevProps.searchParams.get('range') || '7d') ||
                        nextKind !== (prevProps.searchParams.get('kind') || 'all')
                    );
                    if (changesChanged) this.fetchChangesData();
                });
            }
        }
    }

    componentWillUnmount() {
        this.stopWalletPolling();
        if (this.dashboardWs) {
            this.dashboardWs.disconnect();
            this.dashboardWs = null;
        }
        if (this.walletChangeWs) {
            this.walletChangeWs.disconnect();
            this.walletChangeWs = null;
        }
        if (this._resizeListener) window.removeEventListener('resize', this._resizeListener);
    }

    // URL 쿼리 write-through (기본값은 삭제 — URL 클린)
    syncUrl = (patch) => {
        if (!this.props.setSearchParams) return;
        const next = new URLSearchParams(this.props.searchParams);
        const DEFAULTS = {
            status: 'suspended',
            listed: 'true',
            market: null,
            curr: '',
            range: '7d',
            kind: 'all',
        };
        Object.entries(patch).forEach(([k, v]) => {
            const defaultVal = DEFAULTS[k];
            if (v === null || v === undefined || v === '' || String(v) === String(defaultVal)) {
                next.delete(k);
            } else {
                next.set(k, String(v));
            }
        });
        this.props.setSearchParams(next, { replace: true });
    };

    computeDateRange = (preset) => {
        const now = new Date();
        const endIso = now.toISOString();
        if (preset === 'today') {
            const start = new Date(now);
            start.setHours(0, 0, 0, 0);
            return [start.toISOString(), endIso];
        }
        if (preset === '7d') {
            const start = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
            return [start.toISOString(), endIso];
        }
        if (preset === '30d') {
            const start = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
            return [start.toISOString(), endIso];
        }
        return [null, null];
    };

    // ── WebSocket + 폴링 폴백 ──

    connectWebSocket = () => {
        const username = getStoredUsername();
        if (!username) { this.startWalletPolling(); return; }
        this.dashboardWs = new DashboardWebSocket(
            username, getAccessToken,
            this.handleWsMessage,
            this.handleWsStatusChange,
            this.handleWsAuthFailure
        );
        this.dashboardWs.connect();
    };

    connectWalletChangeWs = () => {
        this.walletChangeWs = new WalletChangeWebSocket(this.handleWalletChangeWsMessage);
        this.walletChangeWs.connect();
    };

    handleWalletChangeWsMessage = (message) => {
        if (!message || message.type !== 'wallet_change_update') return;
        this.fetchChangesData();
        this.fetchStatsData();
        this.fetchWalletStatusData();
    };

    handleWsMessage = (message) => {
        try {
            const { data } = message;
            if (!data) return;
            const { data_type, data: payload } = data;
            if (data_type === 'wallet_status_update') {
                const changes = payload.changes || payload;
                const changedIds = Array.isArray(changes)
                    ? changes.map(c => `${c.currency}-${c.net_type}`)
                    : [];
                this.setState({
                    walletStatus: Array.isArray(changes) ? changes : this.state.walletStatus,
                    walletStatusLastUpdated: new Date(),
                    walletHighlightIds: changedIds,
                });
                setTimeout(() => this.setState({ walletHighlightIds: [] }), 3000);
                this.fetchStatsData();
                this.fetchChangesData();
            }
        } catch (err) {
            console.error('[ExchangeNotiV3] WebSocket 메시지 처리 실패:', err);
        }
    };

    handleWsAuthFailure = () => {
        try { clearLoginSession(); } catch (e) {}
        window.location.replace('/login');
    };

    handleWsStatusChange = (status) => {
        if (status === 'connected') {
            this.stopWalletPolling();
            this.setState({ wsStatus: status, isPollingFallback: false });
            this.fetchWalletStatusData();
        } else if (status === 'disconnected') {
            this.startWalletPolling();
            this.setState({ wsStatus: status, isPollingFallback: true });
        } else {
            this.setState({ wsStatus: status });
        }
    };

    fetchWalletStatusData = () => {
        fetchWalletStatus({ exchange: 'upbit', listed_only: this.state.walletListedOnly })
            .then(res => this.setState({
                walletStatus: res.data || [],
                walletStatusLastUpdated: new Date(),
                walletStatusLoading: false,
                walletStatusError: false,
            }))
            .catch(err => {
                console.error('[ExchangeNotiV3] Failed to fetch wallet status:', err);
                showToast({ type: 'error', message: '지갑 상태 로드에 실패했습니다.' });
                this.setState({ walletStatusLoading: false, walletStatusError: true });
            });
    };

    startWalletPolling = () => {
        if (this.walletPollingInterval) return;
        this.fetchWalletStatusData();
        this.walletPollingInterval = setInterval(this.fetchWalletStatusData, 60000);
    };

    stopWalletPolling = () => {
        if (this.walletPollingInterval) {
            clearInterval(this.walletPollingInterval);
            this.walletPollingInterval = null;
        }
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    onWalletSearchChange = (e) => {
        this.setState({ walletSearchQuery: e.target.value });
    };

    onWalletStatusFilterChange = (filter) => {
        this.setState({ walletStatusFilter: filter, walletChangesOffset: 0 });
        this.syncUrl({ status: filter });
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    onWalletListedOnlyChange = (listedOnly) => {
        this.setState({ walletListedOnly: listedOnly, walletChangesOffset: 0 }, () => this.fetchWalletStatusData());
        this.syncUrl({ listed: String(listedOnly) });
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    onWalletMarketFilterChange = (market) => {
        this.setState({ walletMarketFilter: market, walletChangesOffset: 0 });
        this.syncUrl({ market });
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /** 도넛 히어로 정지 KPI 클릭 → suspended 필터 toggle (DAS-40) */
    handleSuspendedKpiClick = () => {
        const isActive = this.state.walletStatusFilter === 'suspended' && this.state.walletMarketFilter === null;
        if (isActive) {
            this.setState({ walletStatusFilter: 'all', walletMarketFilter: null, walletChangesOffset: 0 });
            this.syncUrl({ status: 'all', market: null });
        } else {
            this.setState({ walletStatusFilter: 'suspended', walletMarketFilter: null, walletChangesOffset: 0 });
            this.syncUrl({ status: 'suspended', market: null });
        }
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ── 통계 / 변경 이력 / 서비스 상태 fetch ──

    fetchStatsData = () => {
        // 데이터가 없을 때(첫 로드)만 loading 표시 — WS/폴링 갱신마다 도넛 깜빡임 방지 (DAS-117)
        if (this.state.walletStats == null) this.setState({ walletStatsLoading: true });
        fetchWalletStatusStats()
            .then(res => this.setState({ walletStats: res.data, walletStatsLoading: false, walletStatsError: false }))
            .catch(err => {
                console.error('[ExchangeNotiV3] Failed to fetch wallet stats:', err);
                showToast({ type: 'error', message: '지갑 통계 로드에 실패했습니다.' });
                this.setState({ walletStatsLoading: false, walletStatsError: true });
            });
    };

    fetchChangesData = () => {
        this.setState({ walletChangesLoading: true });
        const params = { limit: 20, offset: this.state.walletChangesOffset };
        if (this.state.walletChangesFilterCurrency) {
            params.currency = this.state.walletChangesFilterCurrency;
        }
        const [dateFrom, dateTo] = this.computeDateRange(this.state.walletChangesRange);
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (this.state.walletChangesKind && this.state.walletChangesKind !== 'all') {
            params.kind = this.state.walletChangesKind;
        }
        fetchWalletStatusChanges(params)
            .then(res => this.setState({
                walletChanges: res.data?.results || [],
                walletChangesPagination: {
                    count: res.data?.count || 0,
                    next: res.data?.next,
                    previous: res.data?.previous,
                },
                walletChangesLoading: false,
                walletChangesError: false,
            }))
            .catch(err => {
                console.error('[ExchangeNotiV3] Failed to fetch wallet changes:', err);
                showToast({ type: 'error', message: '변경 이력 로드에 실패했습니다.' });
                this.setState({ walletChangesLoading: false, walletChangesError: true });
            });
    };

    fetchServiceHealth = () => {
        this.setState({ serviceHealthStatus: 'loading' });
        fetchNotiHealth()
            .then(res => {
                const st = (res.data && res.data.status === 'ok') ? 'ok' : 'error';
                this.setState({ serviceHealthStatus: st });
            })
            .catch(() => this.setState({ serviceHealthStatus: 'error' }));
    };

    onChangesPageChange = (direction) => {
        this.setState(prev => {
            const newOffset = direction === 'next'
                ? prev.walletChangesOffset + 20
                : Math.max(0, prev.walletChangesOffset - 20);
            return { walletChangesOffset: newOffset };
        }, () => this.fetchChangesData());
    };

    onChangesFilterCurrencyChange = (value) => {
        this.setState({ walletChangesFilterCurrency: value, walletChangesOffset: 0 }, () => this.fetchChangesData());
        this.syncUrl({ curr: value });
    };

    onWalletChangesRangeChange = (range) => {
        this.setState({ walletChangesRange: range, walletChangesOffset: 0 }, () => this.fetchChangesData());
        this.syncUrl({ range });
    };

    onWalletChangesKindChange = (kind) => {
        this.setState({ walletChangesKind: kind, walletChangesOffset: 0 }, () => this.fetchChangesData());
        this.syncUrl({ kind });
    };

    render() {
        const {
            collapsed, isMobile,
            walletStats, walletStatsLoading, walletStatsError,
            walletStatus, walletStatusLoading, walletStatusLastUpdated, walletStatusError,
            walletSearchQuery, walletStatusFilter, walletListedOnly, walletMarketFilter,
            walletChanges, walletChangesLoading, walletChangesError, walletChangesPagination,
            walletChangesFilterCurrency, walletChangesRange, walletChangesKind,
            walletHighlightIds,
            wsStatus, isPollingFallback, serviceHealthStatus,
        } = this.state;

        const totalCoins = walletStats?.total_coins ?? '—';
        const normalCount = walletStats?.normal_count ?? '—';
        const suspendedCount = walletStats?.suspended_count ?? '—';
        const changes24h = walletStats?.recent_changes_24h ?? '—';
        const normalRatio = typeof walletStats?.normal_ratio === 'number'
            ? `${walletStats.normal_ratio.toFixed(1)}%`
            : '—';

        // ServiceConnectionIndicator 상태 결합
        let combinedServiceStatus = serviceHealthStatus;
        if (serviceHealthStatus === 'ok' && wsStatus === 'disconnected' && isPollingFallback) {
            combinedServiceStatus = 'degraded';
        } else if (serviceHealthStatus === 'error') {
            combinedServiceStatus = 'error';
        }

        const isLiveWs = wsStatus === 'connected' && !isPollingFallback;
        const suspendedActive = walletStatusFilter === 'suspended' && walletMarketFilter === null;

        // ── 공통 KPI 텍스트 스타일 (도넛 히어로 내 — 시안 V2 KpiValue 30/600) ──
        const heroKpiValueStyle = {
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: 'var(--color-content)',
            lineHeight: 1.05,
        };
        const heroKpiSubStyle = {
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
            color: 'var(--color-content-muted)',
            marginTop: 4,
        };

        // 필터 row 칩 공통 스타일 헬퍼
        const pillStyle = (active) => ({
            display: 'inline-flex',
            alignItems: 'center',
            padding: '5px 12px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: active ? 600 : 500,
            cursor: 'pointer',
            background: active ? 'var(--color-surface-elevated)' : 'var(--color-surface-card)',
            color: active ? 'var(--color-content)' : 'var(--color-content-secondary)',
            border: active
                ? '1px solid var(--color-surface-border-light)'
                : '1px solid var(--color-surface-border)',
            transition: 'all .12s',
            userSelect: 'none',
            whiteSpace: 'nowrap',
        });

        return (
            <div className="min-h-screen w-full overflow-x-hidden bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'
                    }`}
                    style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
                >
                    <MaintenanceBanner />

                    {/* 시안 V2: .app-body { gap: 24 } */}
                    <div
                        className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5"
                        style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 24 }}
                    >
                        {/* ── 페이지 헤더 (시안 V2 TopBar — title "Wallet Ops" + subtitle) ── */}
                        <div>
                            {/* breadcrumb + ServiceConnectionIndicator */}
                            <div style={{
                                fontSize: 11,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: 'var(--color-content-muted)',
                                fontFamily: 'JetBrains Mono, monospace',
                                marginBottom: 4,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                            }}>
                                <span>거래소 · 지갑 상태</span>
                                <ServiceConnectionIndicator status={combinedServiceStatus} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <h1 style={{
                                    fontSize: isMobile ? 18 : 20,
                                    fontWeight: 700,
                                    color: 'var(--color-content)',
                                    letterSpacing: '-0.02em',
                                    margin: 0,
                                }}>
                                    Wallet Ops
                                </h1>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <StatusDot tone={isLiveWs ? 'gain' : 'warning'} size={6} />
                                    <span style={{
                                        fontFamily: 'JetBrains Mono, monospace',
                                        fontSize: 11,
                                        color: 'var(--color-content-muted)',
                                    }}>
                                        {isLiveWs ? 'Live' : '폴링'}
                                    </span>
                                </div>
                            </div>
                            {/* subtitle (시안 V2 .page-sub) */}
                            {!isMobile && (
                                <div style={{
                                    fontSize: 12,
                                    color: 'var(--color-content-secondary)',
                                    marginTop: 4,
                                }}>
                                    거래소 전반의 입출금 상태 · 변경 이력 · 공지사항을 통합
                                </div>
                            )}
                        </div>

                        {/* ── 도넛 히어로 카드 (시안 V2 — gradient + radial glow + KPI grid) ── */}
                        <div style={{
                            padding: isMobile ? 16 : 24,
                            borderRadius: 16,
                            background: 'linear-gradient(135deg, var(--color-surface-card), var(--color-surface-elevated))',
                            border: '1px solid var(--color-surface-border)',
                            position: 'relative',
                            overflow: 'hidden',
                        }}>
                            {/* radial glow — gain-subtle @ 10% 10% */}
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'radial-gradient(circle at 10% 10%, var(--color-gain-subtle), transparent 60%)',
                                pointerEvents: 'none',
                            }} aria-hidden="true" />

                            {/* 모바일: flex row (도넛 + KPI 스택) */}
                            {isMobile ? (
                                <div style={{ position: 'relative', display: 'flex', gap: 16, alignItems: 'center' }}>
                                    {!walletStatsLoading && !walletStatsError && (
                                        <WalletDonut stats={walletStats} isMobile />
                                    )}
                                    {walletStatsLoading && (
                                        <div className="skeleton" style={{
                                            width: 88, height: 88,
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                        }} />
                                    )}
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontFamily: 'JetBrains Mono, monospace',
                                            fontSize: 24,
                                            fontWeight: 600,
                                            letterSpacing: '-0.02em',
                                            color: 'var(--color-content)',
                                        }}>
                                            {normalRatio}
                                        </div>
                                        <div style={{
                                            fontFamily: 'JetBrains Mono, monospace',
                                            fontSize: 10,
                                            color: 'var(--color-content-muted)',
                                            marginTop: 2,
                                        }}>
                                            정상 {normalCount} / {totalCoins}
                                        </div>
                                        {/* 정지 + 24h 변경 — 모바일 가로 배치 */}
                                        <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
                                            <div>
                                                <KpiLabel>정지</KpiLabel>
                                                <button
                                                    onClick={this.handleSuspendedKpiClick}
                                                    aria-pressed={suspendedActive}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        padding: 0,
                                                        cursor: 'pointer',
                                                        fontFamily: 'JetBrains Mono, monospace',
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        color: 'var(--color-loss)',
                                                    }}
                                                >
                                                    {suspendedCount}
                                                </button>
                                            </div>
                                            <div>
                                                <KpiLabel>24h 변경</KpiLabel>
                                                <div style={{
                                                    fontFamily: 'JetBrains Mono, monospace',
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    color: 'var(--color-content)',
                                                }}>
                                                    {changes24h}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* 데스크톱: grid 180px + 1fr×3, gap:28 (시안 V2 정합) */
                                <div style={{
                                    position: 'relative',
                                    display: 'grid',
                                    gridTemplateColumns: '180px 1fr 1fr 1fr',
                                    gap: 28,
                                    alignItems: 'center',
                                }}>
                                    {/* 도넛 (160px) */}
                                    {walletStatsLoading && (
                                        <div className="skeleton" style={{
                                            width: 160, height: 160,
                                            borderRadius: '50%',
                                        }} />
                                    )}
                                    {walletStatsError && (
                                        <div style={{
                                            width: 160, height: 160,
                                            borderRadius: '50%',
                                            background: 'var(--color-loss-subtle)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 11,
                                            color: 'var(--color-loss)',
                                            textAlign: 'center',
                                            padding: 10,
                                        }}>
                                            통계 로드 실패
                                        </div>
                                    )}
                                    {!walletStatsLoading && !walletStatsError && (
                                        <WalletDonut stats={walletStats} size={160} isMobile={false} />
                                    )}

                                    {/* KPI 1: 전체 코인 */}
                                    <div>
                                        <KpiLabel>전체 코인</KpiLabel>
                                        <div style={heroKpiValueStyle}>{totalCoins}</div>
                                        {/* TODO(DAS-106): "8 거래소 기준" sub — 현재 upbit 단일 */}
                                        <div style={heroKpiSubStyle}>upbit 기준</div>
                                    </div>

                                    {/* KPI 2: 정지 코인 — 클릭 진입 (DAS-40) */}
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={this.handleSuspendedKpiClick}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                this.handleSuspendedKpiClick();
                                            }
                                        }}
                                        aria-pressed={suspendedActive}
                                        style={{ cursor: 'pointer', minWidth: 0 }}
                                    >
                                        <KpiLabel>정지 코인</KpiLabel>
                                        <div style={{
                                            ...heroKpiValueStyle,
                                            color: 'var(--color-loss)',
                                        }}>
                                            {suspendedCount}
                                        </div>
                                        {/* TODO(DAS-106): 입금 N · 출금 N 분리 — BE API 미지원 */}
                                        <div style={heroKpiSubStyle}>
                                            정상 {normalCount}
                                        </div>
                                    </div>

                                    {/* KPI 3: 24h 변경 */}
                                    <div>
                                        <KpiLabel>24h 변경</KpiLabel>
                                        <div style={heroKpiValueStyle}>{changes24h}</div>
                                        {/* TODO(DAS-106): 7일 집계 → BE recent_changes_7d 확장 예정 */}
                                        <div style={heroKpiSubStyle}>건</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── 필터 row (시안 V2 — 검색 + 거래소 칩 + 상태 칩 + CSV) ──
                         *   데스크톱 전용. 모바일은 WalletStatusTable 내부 필터로 대체.
                         *   TODO(DAS-106): 거래소 칩 다중 선택 — 현재 upbit 단일 (placeholder pill)
                         *   TODO(DAS-106): CSV export 버튼 — 기능 미구현 placeholder
                         */}
                        {!isMobile && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                            }}>
                                {/* 검색 Input */}
                                <div style={{ width: 280 }}>
                                    <Input
                                        placeholder="코인 · 네트워크 검색..."
                                        value={walletSearchQuery}
                                        onChange={this.onWalletSearchChange}
                                        style={{ height: 36, fontSize: 13 }}
                                        aria-label="코인 또는 네트워크 검색"
                                    />
                                </div>
                                {/* 거래소 칩 — 시안 placeholder (현재 upbit 단일) */}
                                <span style={pillStyle(true)}>전체 거래소</span>
                                {['Upbit', 'Binance', 'OKX', 'Bithumb'].map((ex) => (
                                    <span
                                        key={ex}
                                        style={pillStyle(false)}
                                        title="TODO(DAS-106): 다중 거래소 칩 — 거래소 확장 예정"
                                        aria-disabled="true"
                                    >
                                        {ex}
                                    </span>
                                ))}
                                {/* 상태 필터 칩 */}
                                <div style={{ flex: 1 }} />
                                {['all', 'suspended'].map((f) => {
                                    const labels = { all: '전체', suspended: '정지' };
                                    const isActive = walletStatusFilter === f && walletMarketFilter === null;
                                    return (
                                        <button
                                            key={f}
                                            onClick={() => this.onWalletStatusFilterChange(f)}
                                            aria-pressed={isActive}
                                            style={{
                                                ...pillStyle(isActive),
                                                background: isActive ? 'none' : undefined,
                                                border: 'none',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {labels[f]}
                                        </button>
                                    );
                                })}
                                {/* CSV 버튼 — 시안 placeholder */}
                                <Btn
                                    variant="outline"
                                    size="sm"
                                    icon="download"
                                    title="TODO(DAS-106): CSV export — 기능 미구현"
                                    disabled
                                    style={{ opacity: 0.45, cursor: 'not-allowed' }}
                                >
                                    CSV
                                </Btn>
                            </div>
                        )}

                        {/* ── 신규 상장 공지 (시안 V2 상단 통합) ── */}
                        <div
                            style={{
                                borderRadius: 16,
                                background: 'var(--color-surface-card)',
                                border: '1px solid var(--color-surface-border)',
                                padding: 18,
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                                marginBottom: 12,
                            }}>
                                <div style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    letterSpacing: '-0.01em',
                                    color: 'var(--color-content)',
                                }}>
                                    신규 상장 공지
                                </div>
                            </div>
                            <NewListingNoticesPanel />
                        </div>

                        {/* ── 거래지원 종료 공지 (시안 V2 — 2-col 카드 그리드) ──
                         *   시안: gridTemplateColumns: repeat(2,1fr) gap:12
                         *   우리: DelistingNoticesPanel 재사용 (컴포넌트 내부에서 레이아웃 처리)
                         */}
                        <div
                            style={{
                                borderRadius: 16,
                                background: 'var(--color-surface-card)',
                                border: '1px solid var(--color-surface-border)',
                                padding: 18,
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                                marginBottom: 12,
                            }}>
                                <div style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    letterSpacing: '-0.01em',
                                    color: 'var(--color-content)',
                                }}>
                                    거래지원 종료 공지
                                </div>
                                <span style={{
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: 11,
                                    color: 'var(--color-content-muted)',
                                }}>
                                    모든 거래소
                                </span>
                            </div>
                            <DelistingNoticesPanel />
                        </div>

                        {/* ── 2-col grid: 입출금 상태(1.3fr) + 변경 스트림(1fr) (시안 V2 정합) ──
                         *   모바일: 단일 컬럼 스택, 변경 스트림 우선
                         */}
                        {isMobile ? (
                            <>
                                {/* 모바일 V2: 변경 스트림 → 입출금 순서 */}
                                <WalletChangeStream
                                    changes={walletChanges}
                                    isLiveWs={isLiveWs}
                                    isMobile
                                />
                                <div
                                    style={{
                                        borderRadius: 16,
                                        background: 'var(--color-surface-card)',
                                        border: '1px solid var(--color-surface-border)',
                                        padding: 18,
                                    }}
                                >
                                    <h2 style={{ margin: 0,
                                        fontSize: 13, fontWeight: 600,
                                        letterSpacing: '-0.01em',
                                        color: 'var(--color-content)',
                                        marginBottom: 12,
                                    }}>
                                        입출금 테이블
                                    </h2>
                                    <WalletStatusTable
                                        data={walletStatus}
                                        loading={walletStatusLoading}
                                        lastUpdated={walletStatusLastUpdated}
                                        searchQuery={walletSearchQuery}
                                        statusFilter={walletStatusFilter}
                                        onSearchChange={this.onWalletSearchChange}
                                        onStatusFilterChange={this.onWalletStatusFilterChange}
                                        highlightIds={walletHighlightIds}
                                        error={walletStatusError}
                                        listedOnly={walletListedOnly}
                                        onListedOnlyChange={this.onWalletListedOnlyChange}
                                        marketFilter={walletMarketFilter}
                                        onMarketFilterChange={this.onWalletMarketFilterChange}
                                    />
                                </div>
                            </>
                        ) : (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)',
                                gap: 14,
                                alignItems: 'flex-start',
                            }}>
                                {/* 좌측: 입출금 상태 테이블 */}
                                <div
                                    style={{
                                        borderRadius: 16,
                                        background: 'var(--color-surface-card)',
                                        border: '1px solid var(--color-surface-border)',
                                        padding: 18,
                                    }}
                                >
                                    <h2 style={{ margin: 0,
                                        fontSize: 13, fontWeight: 600,
                                        letterSpacing: '-0.01em',
                                        color: 'var(--color-content)',
                                        marginBottom: 12,
                                    }}>
                                        입출금 상태
                                    </h2>
                                    <WalletStatusTable
                                        data={walletStatus}
                                        loading={walletStatusLoading}
                                        lastUpdated={walletStatusLastUpdated}
                                        searchQuery={walletSearchQuery}
                                        statusFilter={walletStatusFilter}
                                        onSearchChange={this.onWalletSearchChange}
                                        onStatusFilterChange={this.onWalletStatusFilterChange}
                                        highlightIds={walletHighlightIds}
                                        error={walletStatusError}
                                        listedOnly={walletListedOnly}
                                        onListedOnlyChange={this.onWalletListedOnlyChange}
                                        marketFilter={walletMarketFilter}
                                        onMarketFilterChange={this.onWalletMarketFilterChange}
                                    />
                                </div>
                                {/* 우측: 변경 스트림 */}
                                <WalletChangeStream
                                    changes={walletChanges}
                                    isLiveWs={isLiveWs}
                                    isMobile={false}
                                />
                            </div>
                        )}

                        {/* ── 상태 변경 이력 전체 (시안 V2 하단 — kind/curr/페이지네이션) ── */}
                        <div
                            style={{
                                borderRadius: 16,
                                background: 'var(--color-surface-card)',
                                border: '1px solid var(--color-surface-border)',
                                padding: 18,
                            }}
                        >
                            <h2 style={{ margin: 0,
                                fontSize: 13, fontWeight: 600,
                                letterSpacing: '-0.01em',
                                color: 'var(--color-content)',
                                marginBottom: 12,
                            }}>
                                상태 변경 이력 (전체)
                            </h2>
                            <WalletChangeLog
                                changes={walletChanges}
                                loading={walletChangesLoading}
                                pagination={walletChangesPagination}
                                onPageChange={this.onChangesPageChange}
                                filterCurrency={walletChangesFilterCurrency}
                                onFilterCurrencyChange={this.onChangesFilterCurrencyChange}
                                error={walletChangesError}
                                range={walletChangesRange}
                                onRangeChange={this.onWalletChangesRangeChange}
                                kind={walletChangesKind}
                                onKindChange={this.onWalletChangesKindChange}
                            />
                        </div>
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

export default withRouter(ExchangeNotiPageV3);
